import { formatIsoDate } from "../../../../../lib/format";
import { INCOME_TYPE_OPTIONS } from "../constants";
import { formatPhoneFromRaw } from "../fields/PhoneNumberInput";
import { VERIFICATION_OUTCOME } from "./callScript.savePayload";
import {
  type CallScriptEntry,
  type CallScriptHistory,
  type CallScriptHistoryAnswer,
  type CallScriptVerifiedFields,
  EMPTY_CALL_SCRIPT_VERIFIED_FIELDS,
} from "./callScript.types";
import { CALL_SCRIPT_QUESTION_TEXT } from "./callScriptQuestions";

// ---------------------------------------------------------------------------
// callScript.mapping
//
// Pure mappers for the recap-service Call Script contract, forwarded verbatim
// by getHistory1/saveCallScript (both typed `void` on the generated client —
// there is no BFF response schema, so the shape below is taken from a real
// sample payload, not the client's types). recap has no flat `verifiedFields`
// object of its own: everything besides the handful of top-level fields
// (phoneNumberDialed, hireDate, isVerificationOutsourced, isPhoneNoBad) is
// reconstructed from `answers[]`, matched by question text against
// CALL_SCRIPT_QUESTION_TEXT. No React. Unit-testable and side-effect-free.
// Every field is read defensively — unknown/missing keys degrade to an empty
// string/array rather than throwing, so BFF field-name drift never crashes
// the UI.
// ---------------------------------------------------------------------------

/**
 * Narrows an unknown payload to a string-keyed record, or `{}` when it is not a
 * plain object (null, array, primitive).
 * @param {unknown} value - The raw payload.
 * @returns {Record<string, unknown>} The record, or an empty object.
 */
export function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/**
 * Reads the first present key from a record as a string, defaulting to "".
 * @param {Record<string, unknown>} source - The entry payload.
 * @param {...string} keys - Candidate field names in priority order.
 * @returns {string} The string value, or "".
 */
export function readField(source: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== "") {
      return String(value);
    }
  }
  return "";
}

/**
 * Reads the first present key from a record as a number, defaulting to `undefined`.
 * @param {Record<string, unknown>} source - The entry payload.
 * @param {...string} keys - Candidate field names in priority order.
 * @returns {number | undefined} The number value, or `undefined`.
 */
export function readNumber(source: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "number" && !Number.isNaN(value)) {
      return value;
    }
  }
  return undefined;
}

/**
 * Reads a "Y"/"N" flag from a record, defaulting to "N".
 * @param {Record<string, unknown>} source - The entry payload.
 * @param {string} key - The flag's field name.
 * @returns {"Y" | "N"} The flag value.
 */
export function readYesNo(source: Record<string, unknown>, key: string): "Y" | "N" {
  return source[key] === "Y" ? "Y" : "N";
}

/**
 * Maps a raw `answers` array entry defensively, degrading unknown/missing
 * keys rather than throwing.
 * @param {unknown} answer - One raw answer record.
 * @returns {CallScriptHistoryAnswer} The mapped answer.
 */
function mapHistoryAnswer(answer: unknown): CallScriptHistoryAnswer {
  const row = asRecord(answer);
  return {
    verificationAnswerPk: readNumber(row, "verificationAnswerPk") ?? 0,
    verificationQuestionPk: readNumber(row, "verificationQuestionPk") ?? 0,
    questionText: readField(row, "questionText"),
    answerEntered: readField(row, "answerEntered"),
    answerStored: readField(row, "answerStored"),
    isAnswerMatch: readYesNo(row, "isAnswerMatch"),
  };
}

/** Verified fields with no counterpart in the question catalog — never reconstructable from `answers[]`. */
const QUESTIONLESS_VERIFIED_FIELDS = new Set<keyof CallScriptVerifiedFields>([
  "employerName",
  "phoneNumberDialed",
  "verificationOutsourced",
  "originalNumberBad",
  "hireDate",
  "differentWorkLocation",
  "workLocationName",
  "workLocationStreetNumber",
  "workLocationStreetName",
  "workLocationApartmentNumber",
  "workLocationZip",
  "workLocationCity",
  "workLocationState",
]);

/** Verified fields whose answer text is numeric rather than a plain string. */
const NUMERIC_VERIFIED_FIELDS = new Set<keyof CallScriptVerifiedFields>(["grossPayPerPeriod", "avgHoursPerWeek"]);

/** Lowercased question text -> verified field, built once from {@link CALL_SCRIPT_QUESTION_TEXT}. */
const FIELD_BY_QUESTION_TEXT = new Map<string, keyof CallScriptVerifiedFields>(
  (Object.entries(CALL_SCRIPT_QUESTION_TEXT) as Array<[keyof CallScriptVerifiedFields, string]>)
    .filter(([field]) => !QUESTIONLESS_VERIFIED_FIELDS.has(field))
    .map(([field, text]) => [text.toLowerCase(), field])
);

/**
 * Reconstructs `CallScriptVerifiedFields` from a history entry's top-level
 * fields (`employerName` has no BFF counterpart and is intentionally left
 * blank — the modal only ever reads it from the saved Employment record) plus
 * its `answers[]`, matched against the question catalog by text. Fields with
 * no question counterpart (e.g. Physical Work Location address fields) are
 * never persisted and always come back blank.
 * @param {Record<string, unknown>} row - The raw history entry.
 * @param {CallScriptHistoryAnswer[]} answers - That entry's mapped answers.
 * @returns {CallScriptVerifiedFields} The reconstructed verified fields.
 */
function mapVerifiedFields(row: Record<string, unknown>, answers: CallScriptHistoryAnswer[]): CallScriptVerifiedFields {
  const verifiedFields: CallScriptVerifiedFields = {
    ...EMPTY_CALL_SCRIPT_VERIFIED_FIELDS,
    phoneNumberDialed: readField(row, "phoneNumberDialed"),
    hireDate: readField(row, "hireDate"),
    verificationOutsourced: readYesNo(row, "isVerificationOutsourced") === "Y",
    originalNumberBad: readYesNo(row, "isPhoneNoBad") === "Y",
  };

  for (const answer of answers) {
    const field = FIELD_BY_QUESTION_TEXT.get(answer.questionText.toLowerCase());
    if (!field || !answer.answerEntered) {
      continue;
    }
    if (NUMERIC_VERIFIED_FIELDS.has(field)) {
      const parsed = Number(answer.answerEntered);
      (verifiedFields as unknown as Record<string, unknown>)[field] = Number.isNaN(parsed) ? "" : parsed;
    } else {
      (verifiedFields as unknown as Record<string, unknown>)[field] = answer.answerEntered;
    }
  }

  return verifiedFields;
}

/**
 * Maps a single raw history entry (recap's Call Script call record) to the
 * app's view model.
 * @param {unknown} entry - One raw history record.
 * @returns {CallScriptEntry} The mapped entry.
 */
export function mapCallScriptEntry(entry: unknown): CallScriptEntry {
  const row = asRecord(entry);
  const answers = Array.isArray(row.answers) ? row.answers.map((answer) => mapHistoryAnswer(answer)) : [];
  return {
    verificationPhoneCallPk: readNumber(row, "verificationPhoneCallPk") ?? 0,
    verificationOutcome: readField(row, "verificationOutcome"),
    phoneNumberDialed: readField(row, "phoneNumberDialed"),
    isVerificationOutsourced: readYesNo(row, "isVerificationOutsourced"),
    isPhoneNoBad: readYesNo(row, "isPhoneNoBad"),
    hireDate: readField(row, "hireDate"),
    occurredAt: readField(row, "occurredAt"),
    analystCauPk: readNumber(row, "analystCauPk") ?? 0,
    analystName: readField(row, "analystName"),
    applicantEmploymentPk: readNumber(row, "applicantEmploymentPk"),
    applicantAddressPk: readNumber(row, "applicantAddressPk"),
    answers,
    verifiedFields: mapVerifiedFields(row, answers),
  };
}

/**
 * Maps a Call Script history response (recap's body, forwarded verbatim) into
 * entries. Accepts either a bare array of entries or an object wrapping the
 * entries under a common key (`entries` / `history` / `rows`); anything else
 * yields no entries.
 * @param {unknown} payload - The raw `getHistory1` response.
 * @returns {CallScriptHistory} The history view model.
 */
export function mapCallScriptHistory(payload: unknown): CallScriptHistory {
  const entries = Array.isArray(payload)
    ? payload
    : (() => {
        const record = asRecord(payload);
        const candidate = record.entries ?? record.history ?? record.rows;
        return Array.isArray(candidate) ? candidate : [];
      })();
  return { entries: entries.map((entry) => mapCallScriptEntry(entry)) };
}

/**
 * Narrows a Call Script history list down to the entries for one income
 * slot's employer. `getHistory1` is scoped only by applicant, not by income
 * slot, so it returns every verification call the applicant has ever had
 * across all of their employers — the Notes History panel would otherwise
 * mix in another income block's calls. A blank/undefined `employmentId`
 * (no saved Employment record yet, e.g. a brand-new income block) yields no
 * entries rather than falling back to showing everything.
 * @param {CallScriptEntry[]} entries - The full, unfiltered history list.
 * @param {string | undefined} employmentId - This income block's Employment record id.
 * @returns {CallScriptEntry[]} Only the entries recorded against that employment.
 */
export function filterHistoryEntriesForEmployment(
  entries: CallScriptEntry[],
  employmentId: string | undefined
): CallScriptEntry[] {
  if (!employmentId) {
    return [];
  }
  return entries.filter((entry) => String(entry.applicantEmploymentPk ?? "") === employmentId);
}

/**
 * Resolves the "Verification Outcome" label shown on the Income Info page
 * next to one employer block (spec §8) — the `verificationOutcome` recorded
 * on that employment's most recent Call Script call, or "No Attempt" when no
 * call has ever been made against it (no matching history entry, including a
 * brand-new income block with no saved Employment yet). History entries are
 * assumed most-recent-first, matching every other consumer of this endpoint
 * (the modal's own `savedFields`/History panel both take entry `[0]`).
 * @param {CallScriptEntry[]} entries - The full, unfiltered history list for the applicant.
 * @param {string | undefined} employmentId - This income block's Employment record id.
 * @returns {string} One of the {@link VERIFICATION_OUTCOME} values.
 */
export function resolveLatestVerificationOutcome(entries: CallScriptEntry[], employmentId: string | undefined): string {
  const [latest] = filterHistoryEntriesForEmployment(entries, employmentId);
  return latest?.verificationOutcome || VERIFICATION_OUTCOME.NO_ATTEMPT;
}

/**
 * Maps a verification-outcome label to its Income Info page display color
 * (spec §8.2): green for a contact that reached someone with no red flags
 * (RPC / No RPC - No Issues), red for no attempt at all or a confirmed-bad
 * dialed number (Not Fundable) — including any unrecognized/blank value,
 * which defaults to red the same as "No Attempt".
 * @param {string} outcome - A {@link VERIFICATION_OUTCOME} value.
 * @returns {"red" | "green"} The display color.
 */
export function verificationOutcomeColor(outcome: string): "red" | "green" {
  return outcome === VERIFICATION_OUTCOME.RPC || outcome === VERIFICATION_OUTCOME.NO_RPC_NO_ISSUES ? "green" : "red";
}

/** Emp Type code -> label, built once from {@link INCOME_TYPE_OPTIONS}. */
const EMPLOYMENT_TYPE_LABEL_BY_CODE = new Map<string, string>(
  INCOME_TYPE_OPTIONS.map((option) => [option.value, option.label])
);

/** Question text for every phone-valued question, formatted xxx-xxx-xxxx for display. */
const PHONE_QUESTION_TEXTS = new Set<string>(
  [
    CALL_SCRIPT_QUESTION_TEXT.phoneNumberDialed,
    CALL_SCRIPT_QUESTION_TEXT.employerPhone,
    CALL_SCRIPT_QUESTION_TEXT.workLocationPhone,
  ].filter((text): text is string => text !== undefined)
);

/**
 * Resolves a Notes History answer's display value: the Emp Type question's
 * `answerEntered` is a raw BFF code ("6"-"11"), not a label a CA user would
 * recognize, so it is remapped through {@link INCOME_TYPE_OPTIONS} (the same
 * catalog backing the Emp Type dropdown). Phone questions (Phone Number
 * Dialed, Employer Verification Phone, Physical Work Location Phone) come
 * back as raw digits — formatted xxx-xxx-xxxx to match how the modal's own
 * Verified/Saved columns display a phone value. Every other question's
 * answer is already human-readable and is returned verbatim. An unrecognized
 * Emp Type code falls back to the raw value rather than rendering blank. The
 * Hire Date question comes back as a raw `YYYY-MM-DD` string — formatted
 * MM/DD/YYYY to match how the modal's own Verified/Saved date fields display.
 * @param {CallScriptHistoryAnswer} answer - The history answer to display.
 * @returns {string} The value to render for that answer.
 */
export function formatHistoryAnswerValue(answer: CallScriptHistoryAnswer): string {
  if (answer.questionText === CALL_SCRIPT_QUESTION_TEXT.employmentType) {
    return EMPLOYMENT_TYPE_LABEL_BY_CODE.get(answer.answerEntered) ?? answer.answerEntered;
  }
  if (PHONE_QUESTION_TEXTS.has(answer.questionText) && answer.answerEntered !== "") {
    return formatPhoneFromRaw(answer.answerEntered);
  }
  if (answer.questionText === CALL_SCRIPT_QUESTION_TEXT.hireDate && answer.answerEntered !== "") {
    return formatIsoDate(answer.answerEntered);
  }
  return answer.answerEntered;
}
